import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Copy, KeyRound, Award, Mail, Phone } from "lucide-react";

const EMPTY = {
  name: "", email: "", phone: "",
  temp_password: "Coach@12345",
  sport: "cricket", role_title: "Head Coach",
  bio: "", photo_url: "",
};

const SPORTS = ["cricket", "football", "badminton", "gym", "swimming", "other"];

export default function AdminCoaches() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | coach
  const [form, setForm] = useState(EMPTY);
  const [pwReset, setPwReset] = useState(null);
  const [issuedCred, setIssuedCred] = useState(null); // { email, temp_password }

  const load = () => api.get("/admin/coaches").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, temp_password: "" }); };

  const save = async () => {
    try {
      if (editing === "new") {
        if (!form.temp_password || form.temp_password.length < 6) return toast.error("Password must be ≥6 chars");
        const { data } = await api.post("/admin/coaches", form);
        toast.success("Coach created");
        setIssuedCred({ email: data.email, temp_password: form.temp_password });
      } else {
        const patch = { name: form.name, phone: form.phone, sport: form.sport, role_title: form.role_title, bio: form.bio, photo_url: form.photo_url };
        await api.patch(`/admin/coaches/${editing.id}`, patch);
        toast.success("Coach updated");
      }
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete coach ${c.name}? Any batch assignments will be unlinked.`)) return;
    try { await api.delete(`/admin/coaches/${c.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (c) => {
    try { await api.patch(`/admin/coaches/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const resetPassword = async () => {
    if (!pwReset?.newPw || pwReset.newPw.length < 6) return toast.error("Password must be ≥6 chars");
    try {
      await api.patch(`/admin/coaches/${pwReset.coach.id}`, { reset_password: pwReset.newPw });
      setIssuedCred({ email: pwReset.coach.email, temp_password: pwReset.newPw });
      setPwReset(null);
      toast.success("Password reset");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div data-testid="admin-coaches">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Academy · Coaches</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none flex items-center gap-3">
            <Award className="brand-blue" size={32} /> Coaches.
          </h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">Create login accounts for coaches. Each coach can access <span className="brand-blue">/coach/login</span>, see their assigned batches, mark attendance, and view parent contacts. Assign a coach to a batch from <span className="brand-blue">/admin/batches</span>.</p>
        </div>
        <button onClick={openNew} data-testid="new-coach-btn" className="bg-brand-blue text-white px-5 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:glow-blue">
          <Plus size={12} /> Add Coach
        </button>
      </div>

      {issuedCred && (
        <div className="mb-6 border border-brand-orange/40 bg-brand-orange/10 p-5" data-testid="issued-creds">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-2">// Share these credentials with the coach (shown once)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-mono text-white/50 text-xs w-20">Email</span>
              <code className="brand-blue font-mono flex-1 break-all">{issuedCred.email}</code>
              <button onClick={() => copy(issuedCred.email)} className="p-1.5 border border-white/20 hover:border-brand-orange"><Copy size={12} /></button>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-white/50 text-xs w-20">Password</span>
              <code className="brand-orange font-mono flex-1">{issuedCred.temp_password}</code>
              <button onClick={() => copy(issuedCred.temp_password)} className="p-1.5 border border-white/20 hover:border-brand-orange"><Copy size={12} /></button>
            </div>
          </div>
          <button onClick={() => setIssuedCred(null)} className="mt-3 text-[10px] font-mono uppercase tracking-widest text-white/60 hover:text-white">Dismiss</button>
        </div>
      )}

      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
              <th className="text-left p-4">Coach</th>
              <th className="text-left p-4">Contact</th>
              <th className="text-left p-4">Sport</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`coach-row-${c.id}`} className="border-b border-white/5">
                <td className="p-4">
                  <div className="font-bold">{c.name}</div>
                  <div className="text-xs text-white/50 font-mono">{c.role_title || "Coach"}</div>
                </td>
                <td className="p-4 text-xs">
                  <div className="flex items-center gap-2 text-white/70"><Mail size={11} className="brand-blue" /> {c.email}</div>
                  {c.phone && <div className="flex items-center gap-2 text-white/70 mt-1"><Phone size={11} className="brand-orange" /> {c.phone}</div>}
                </td>
                <td className="p-4 font-mono text-xs uppercase tracking-widest brand-blue">{c.sport || "—"}</td>
                <td className="p-4">
                  <button onClick={() => toggleActive(c)} data-testid={`toggle-${c.id}`}
                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${c.active ? "bg-brand-blue/20 text-brand-blue" : "bg-white/10 text-white/40"}`}>
                    {c.active ? "active" : "inactive"}
                  </button>
                  {c.must_change_password && <div className="text-[10px] brand-orange font-mono mt-1">First-login pending</div>}
                </td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => openEdit(c)} data-testid={`edit-${c.id}`} className="text-white/60 hover:brand-blue"><Edit3 size={14} /></button>
                  <button onClick={() => setPwReset({ coach: c, newPw: "" })} data-testid={`reset-${c.id}`} className="text-white/60 hover:brand-orange"><KeyRound size={14} /></button>
                  <button onClick={() => del(c)} data-testid={`del-${c.id}`} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-white/40">No coaches yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">{editing === "new" ? "Add Coach" : "Edit Coach"}</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="coach-form-name" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (login id)" data-testid="coach-form-email" disabled={editing !== "new"} className="w-full bg-transparent border border-white/20 py-3 px-4 disabled:opacity-50" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (+91…)" data-testid="coach-form-phone" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} data-testid="coach-form-sport" className="bg-[#131A2B] border border-white/20 py-3 px-4">
                  {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} placeholder="Title (e.g. Head Coach)" className="bg-transparent border border-white/20 py-3 px-4" />
              </div>
              <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Short bio (optional)" rows={3} className="w-full bg-transparent border border-white/20 py-3 px-4 text-sm" />
              {editing === "new" && (
                <input value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} placeholder="Temporary password (coach will change on first login)" data-testid="coach-form-pw" className="w-full bg-transparent border border-brand-orange/40 py-3 px-4 font-mono text-sm brand-orange" />
              )}
              <button onClick={save} data-testid="coach-form-submit" className="w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest hover:glow-blue">
                {editing === "new" ? "Create login" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pwReset && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPwReset(null)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">Reset password</h3>
              <button onClick={() => setPwReset(null)}><X size={18} /></button>
            </div>
            <p className="text-sm text-white/60 mb-4">Set a new temporary password for <span className="brand-blue">{pwReset.coach.name}</span>. They&apos;ll be forced to change it on next login.</p>
            <input type="text" value={pwReset.newPw} onChange={(e) => setPwReset({ ...pwReset, newPw: e.target.value })} placeholder="New temporary password" data-testid="coach-pw-reset-input"
              className="w-full bg-transparent border border-brand-orange/40 py-3 px-4 font-mono text-sm brand-orange mb-4" />
            <button onClick={resetPassword} data-testid="coach-pw-reset-submit" className="w-full bg-brand-orange text-black py-3 font-bold uppercase tracking-widest">Reset & Issue</button>
          </div>
        </div>
      )}
    </div>
  );
}
